import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, X } from "lucide-react";
import axios from "../../services/AxiosInstance";
import { getApiUrl } from "../../utils/ApiUtils";
import { useFeatures } from "../../context/FeatureContext";

/**
 * Dismissible reminder banner: shown when the company has the AI agent (Finn) enabled but has
 * NOT configured an e-mail address for Finn to send approval requests / reminders / the CFO
 * brief to. Without it the agent platform skips sending (there is no default recipient), so the
 * user would silently miss notifications — this nudges them to set one.
 *
 * Dismiss = snooze: hidden for SNOOZE_DAYS, then it reappears as a reminder (per company, via
 * localStorage). Only renders when the agent feature is on AND the email is unset.
 */

const SNOOZE_DAYS = 7;
const snoozeKey = (companyId) => `agentEmailBanner.snoozeUntil.${companyId}`;

function isSnoozed(companyId) {
  try {
    const until = Number(localStorage.getItem(snoozeKey(companyId)) || 0);
    return Boolean(until) && Date.now() < until;
  } catch {
    return false;
  }
}

export default function AgentEmailBanner({ companyId }) {
  const { features, hasLoaded } = useFeatures();
  const agentEnabled = Boolean(features && features.ai_invoice_agent === true);

  // `configured` starts true so the banner never flashes before we know the answer.
  const [status, setStatus] = useState({ loading: true, configured: true });
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setStatus({ loading: true, configured: true });
    setDismissed(false);
    // No need to hit the API when it can't show anyway.
    if (!companyId || !agentEnabled || !hasLoaded || isSnoozed(companyId)) return undefined;

    (async () => {
      try {
        const res = await axios.get(getApiUrl(`${companyId}/settings/invoice_agent_settings`), {
          withCredentials: true,
        });
        let email = "";
        if (res.data?.value) {
          const val = typeof res.data.value === "string" ? JSON.parse(res.data.value) : res.data.value;
          email = (val && val.approverEmail) || "";
        }
        if (!cancelled) setStatus({ loading: false, configured: Boolean(String(email).trim()) });
      } catch (err) {
        // 404 = the setting was never created → treat as unconfigured. Other errors: stay quiet.
        if (!cancelled) setStatus({ loading: false, configured: err?.response?.status ? err.response.status !== 404 : true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId, agentEnabled, hasLoaded]);

  if (!companyId || !agentEnabled || !hasLoaded) return null;
  if (status.loading || status.configured || dismissed || isSnoozed(companyId)) return null;

  const onDismiss = () => {
    try {
      localStorage.setItem(snoozeKey(companyId), String(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000));
    } catch {
      /* ignore storage failures — just hide for this session */
    }
    setDismissed(true);
  };

  return (
    <div
      role="status"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
        background: "#FFF4E0",
        border: "1px solid #FFE0A6",
        borderRadius: 12,
        padding: "14px 16px",
        margin: "0 0 20px",
      }}
    >
      <div
        style={{
          flex: "0 0 auto",
          width: 38,
          height: 38,
          borderRadius: 10,
          background: "#FFE7B8",
          color: "#9a6400",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Mail size={20} />
      </div>

      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: "#7a4f00", marginBottom: 2 }}>
          Finn saknar en e-postadress för avisering
        </div>
        <div style={{ fontSize: "0.9rem", color: "#5c4a25", lineHeight: 1.5 }}>
          Du har inte angett vilken e-postadress Finn ska skicka godkännanden, påminnelser och
          CFO-brev till. Tills dess kan inga aviseringar skickas.
        </div>
        <div style={{ marginTop: 10, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <Link
            to={`/${companyId}/settings/agents#godkannare`}
            style={{
              display: "inline-block",
              background: "var(--primary, #44814E)",
              color: "#fff",
              textDecoration: "none",
              fontWeight: 600,
              fontSize: "0.88rem",
              padding: "8px 16px",
              borderRadius: 8,
            }}
          >
            Konfigurera nu
          </Link>
          <span style={{ fontSize: "0.78rem", color: "#8a7340" }}>
            Påminnelsen visas igen om {SNOOZE_DAYS} dagar.
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={onDismiss}
        aria-label="Stäng påminnelse"
        title="Påminn mig senare"
        style={{
          flex: "0 0 auto",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          color: "#9a6400",
          padding: 4,
          lineHeight: 0,
        }}
      >
        <X size={18} />
      </button>
    </div>
  );
}
